import React from "react"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import {
  Box,
  TextField,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material"
import { Button } from "../components"
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom"
import { useSnackbar } from "../context/SnackbarContext"
import api from "../api/axiosInstance"
import Logo from "../assets/holding-hands.svg"
import { MdEmail, MdLock, MdNumbers, MdVisibility, MdVisibilityOff } from "react-icons/md"

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  otp: Yup.string()
    .required("OTP is required")
    .matches(/^\d+$/, "OTP must contain only digits")
    .min(4, "OTP must be at least 4 digits")
    .max(6, "OTP cannot be more than 6 digits"),
  new_password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
    ),
  confirm_password: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("new_password")], "Passwords must match"),
})

const ResetPassword: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showSnackbar } = useSnackbar()
  const [loading, setLoading] = React.useState(false)
  const [showPassword, setShowPassword] = React.useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false)

  // Get email from location state if available
  const initialEmail = location.state?.email || ""

  const handleSubmit = async (values: {
    email: string
    otp: string
    new_password: string
    confirm_password: string
  }) => {
    setLoading(true)
    try {
      const { email, otp, new_password } = values
      await api.post("/auth/reset-password/", {
        email,
        otp,
        new_password,
      })

      showSnackbar("Password reset successful!", "success")

      // Redirect to login page after successful password reset
      setTimeout(() => {
        navigate("/")
      }, 1500)
    } catch (e: any) {
      const errorMessage = e?.response?.data?.detail || 
                          e?.response?.data?.message || 
                          "Password reset failed. Please try again."
      showSnackbar(errorMessage, "error")
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePassword = () => {
    setShowPassword(!showPassword)
  }

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "linear-gradient(140deg, #eaf3ff 0%, #f5f8ff 100%)",
        p: 2,
      }}
    >
      <Paper
        elevation={5}
        sx={{
          width: { xs: "100%", sm: 420 },
          padding: "10px 30px 20px 30px",
          borderRadius: 5,
          backgroundColor: "#ffffff",
        }}
      >
        <Stack alignItems="center" textAlign="center">
          <Box
            component="img"
            // @ts-ignore
            src={Logo}
            alt="Organization logo"
            sx={{ height: 120 }}
          />
          <Typography variant="h4" fontWeight="600" sx={{ color: "#212B36" }}>
            Reset Password
          </Typography>
          <Typography sx={{ color: "#637381", fontSize: 16, mb: 1 }}>
            Enter the OTP code sent to your email and create a new password
          </Typography>
        </Stack>

        <Formik
          initialValues={{
            email: initialEmail,
            otp: "",
            new_password: "",
            confirm_password: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form noValidate>
              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                id="email"
                name="email"
                type="email"
                label="Email address"
                InputProps={{
                  startAdornment: <MdEmail style={{ marginRight: 8, color: "#637381" }} />,
                }}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />

              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                id="otp"
                name="otp"
                label="OTP Code"
                InputProps={{
                  startAdornment: <MdNumbers style={{ marginRight: 8, color: "#637381" }} />,
                }}
                error={touched.otp && Boolean(errors.otp)}
                helperText={touched.otp && errors.otp}
              />

              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                id="new_password"
                name="new_password"
                type={showPassword ? "text" : "password"}
                label="New Password"
                InputProps={{
                  startAdornment: <MdLock style={{ marginRight: 8, color: "#637381" }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="toggle password visibility" onClick={handleTogglePassword} edge="end">
                        {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={touched.new_password && Boolean(errors.new_password)}
                helperText={touched.new_password && errors.new_password}
              />

              <Field
                as={TextField}
                variant="outlined"
                margin="normal"
                fullWidth
                id="confirm_password"
                name="confirm_password"
                type={showConfirmPassword ? "text" : "password"}
                label="Confirm Password"
                InputProps={{
                  startAdornment: <MdLock style={{ marginRight: 8, color: "#637381" }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleToggleConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={touched.confirm_password && Boolean(errors.confirm_password)}
                helperText={touched.confirm_password && errors.confirm_password}
              />

              <Button type="submit" fullWidth sx={{ mt: 3 }} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : "Reset Password"}
              </Button>

              <Typography variant="body2" align="center">
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  underline="hover"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 2,
                    color: "#1A73E8",
                    fontWeight: 600,
                  }}
                >
                  Back to forgot password
                </Link>
              </Typography>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  )
}

export default ResetPassword
